import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getRFXQuestionsForRequest,
  getRFXRequestAuditRecords,
  loadRFXRequest,
  transitionRFXRequest,
  TransitionRFXRequestInput
} from 'gql';
import { RFIRequestForm } from 'interfaces/RFIRequestForm';
import { RFXRequestAuditRecord } from 'interfaces/RFXAuditRecordModel';
import { RFXQuestion } from 'interfaces/RFXQuestionModel';
import { RFXRequestState } from 'interfaces/RFXRequestForm';
import { VendorStatus } from 'rfxportal/pages/ViewPages/BuyerRFXDetailView/components/SuppliersCorrespondence/types';
import { setCurrentRound, setRequestId } from 'rfxportal/reducers/BuyerViewPageSlice';
import { setCurrentRFXId, setCurrentRound as setRequestFormCurrentRound } from 'rfxportal/reducers/RFXRequestFormSlice';
import { AppDispatch, RootState } from 'store';
import { UIInput } from 'types/uiTypes';
import { convertSystemErrorToString } from 'util/errorUtils';
import {
  createBuyerRFIQuestion,
  createVendorQuestionMap,
  generateVendorStatusArray,
  hasNoPendingQuestionAndDocuments
} from 'util/questionUtils';
import { extractPropertyOrElseDefault } from 'util/RFXUtils';
import { convertRFXRequestToRFIRequestForm } from 'util/transformers';

import { RequestPropertyIds } from '../../../data/BackendEntityConstants';
import { createRFIReplyContent } from '../common/rfiActions';
import { OnSendRFIMessageUIInput, PendingQuestionProps } from '../common/rfiTypes';
import { NEW_RFI_REQUEST_FORM } from '../rfiWizardSlice/constants';

export const SLICE_NAME = 'rfiBuyerDetailView';

/**
 * Redux slice of BuyerRFIDetailView page
 *
 * TODO: move onPublishRFI, onTransitionRFI from rifWizardSlice to this
 */
export interface RFIBuyerDetailViewState {
  username: string;
  rfiRequestForm: RFIRequestForm;
  /**
   * The action of transition RFI to "Discarded" or "Completed" state share the same UI. Use this to 
   * distinguish which action to take.
   * 
   * The caller should set this before show RFITransitionModal, and not clear it after the modal is
   * dismissed, so that the Alert in the buyer details view can use this to show the correct message,
   * such as: "This request has been discarded."
   */
  transitionToState: RFXRequestState;
  transitionReason: string;
  selectedVendorId: string;
  vendorQuestionMap: Record<string, RFXQuestion[]>;
  // When a new question is just created, we need to make sure it is created in the backend,
  // and related documents are attached. Before that, it is the pending question.
  pendingQuestion: PendingQuestionProps | null;
  auditRecords: RFXRequestAuditRecord[];
  // Items for the "Supplier correspondence" table
  correspondenceTableItems: VendorStatus[];
  status: {
    // Whether detail view page is initiating
    isInitiatingDetailView: boolean;
    // Whether supplier clicked the "send" button and back-end is processing
    isSendingMessage: boolean;
    // Whether the page is refreshing
    isRefreshingPage: boolean;
    // Whether RFI is in the process of being published. This is triggered by clicking
    // "Publish RFI" from buyer details view.
    isPublishRFI: boolean;
    // Whether RFI is in state transition
    isTransitionRFI: boolean;
  };
  visibility: {
    // Whether the PublishModal in buyer details view is visible.
    isPublishRFIModalVisible: boolean;
    // Whether the TransitionModal in buyer details view is visible.
    isTransitionModalVisible: boolean;
    // Whether the information Alert is visible after RFI is published.
    showRFIPublished: boolean;
    // Whether the information Alert is visible after RFI is transitioned.
    showRFITransitioned: boolean;
    // Whether to show RFI is in draft state and remind buyer to publish it.
    showRFIInDraftState: boolean;
    // Whether to show alert that RFI is in discarded state.
    showRFIInDiscardedState: boolean;
    // Whether correspondence modal is visible
    isCorrespondenceModalVisible: boolean;
  };
  errors: {
    initiatingDetailViewError: string;
    sendMessageError: string;
    refreshPageError: string;
    publishRFIError: string;
    transitionError: string;
  };
}

interface SetStatusParam {
  statusKey: keyof RFIBuyerDetailViewState['status'];
  status: RFIBuyerDetailViewState['status'][keyof RFIBuyerDetailViewState['status']];
}

interface SetErrorParam {
  errorKey: keyof RFIBuyerDetailViewState['errors'];
  error: RFIBuyerDetailViewState['errors'][keyof RFIBuyerDetailViewState['errors']];
}

interface SetVisibilityParam {
  visibilityKey: keyof RFIBuyerDetailViewState['visibility'];
  visibility: RFIBuyerDetailViewState['visibility'][keyof RFIBuyerDetailViewState['visibility']];
}

const initialState: RFIBuyerDetailViewState = {
  username: '',
  selectedVendorId: '',
  rfiRequestForm: NEW_RFI_REQUEST_FORM,
  transitionToState: RFXRequestState.DRAFT,
  transitionReason: '',
  vendorQuestionMap: {},
  pendingQuestion: null,
  auditRecords: [],
  correspondenceTableItems: [],
  status: {
    isInitiatingDetailView: false,
    isSendingMessage: false,
    isRefreshingPage: false,
    isPublishRFI: false,
    isTransitionRFI: false
  },
  errors: {
    initiatingDetailViewError: '',
    sendMessageError: '',
    refreshPageError: '',
    publishRFIError: '',
    transitionError: ''
  },
  visibility: {
    isPublishRFIModalVisible: false,
    showRFIPublished: false,
    showRFIInDraftState: false,
    showRFIInDiscardedState: false,
    showRFITransitioned: false,
    isTransitionModalVisible: false,
    isCorrespondenceModalVisible: false
  }
};

const rfiBuyerDetailViewSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setInitializedState(state, action: PayloadAction<Partial<RFIBuyerDetailViewState>>) {
      return {
        ...initialState,
        ...action.payload
      };
    },
    setRFIRequestForm(state, action) {
      state.rfiRequestForm = action.payload;
    },
    setTransitionToState(state, action) {
      state.transitionToState = action.payload;
    },
    setTransitionReason(state, action) {
      state.transitionReason = action.payload;
    },
    setSelectedVendorId(state, action) {
      state.selectedVendorId = action.payload;
    },
    setPendingQuestion(state, action) {
      state.pendingQuestion = action.payload;
    },
    setVendorQuestionMap(state, action) {
      state.vendorQuestionMap = action.payload;
    },
    setAuditRecords(state, action) {
      state.auditRecords = action.payload;
    },
    clearPendingQuestion(state) {
      state.pendingQuestion = null;
    },
    setRFIBuyerDetailViewStatus(state, action: PayloadAction<SetStatusParam>) {
      const { statusKey, status } = action.payload;
      return {
        ...state,
        status: {
          ...state.status,
          [statusKey]: status
        }
      };
    },
    setRFIBuyerDetailViewError(state, action: PayloadAction<SetErrorParam>) {
      const { errorKey, error } = action.payload;
      return {
        ...state,
        errors: {
          ...state.errors,
          [errorKey]: error
        }
      };
    },
    setRFIBuyerDetailViewVisibility(state, action: PayloadAction<SetVisibilityParam>) {
      const { visibilityKey, visibility } = action.payload;
      return {
        ...state,
        visibility: {
          ...state.visibility,
          [visibilityKey]: visibility
        }
      };
    }
  }
});
export interface InitializeDetailViewUIInput extends UIInput {
  rfxId: string;
  username: string;
}

/**
 * Initialize RFI buyer detail view state, which includes related RFX request, and
 * Supplier's effective vendorId.
 */
export const onInitializeBuyerDetailView = createAsyncThunk<
  void,
  InitializeDetailViewUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onInitializeBuyerDetailView`,
  async ({ apolloClient, rfxId, username }, { dispatch }): Promise<void> => {
    try {
      dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isInitiatingDetailView', status: true }));

      // TODO: revisit this, do we need this?
      dispatch(
        setRequestId({
          id: rfxId,
          revision: 1
        })
      );
      dispatch(setCurrentRound(1));
      dispatch(setCurrentRFXId(rfxId));
      dispatch(setRequestFormCurrentRound(1));

      const rfxRequest = await loadRFXRequest({ apolloClient, rfxId, revision: 1 });
      const rfiRequestForm = convertRFXRequestToRFIRequestForm(rfxRequest);

      if (rfiRequestForm.state === RFXRequestState.DRAFT) {
        dispatch(setInitializedState({ username, rfiRequestForm }));
        dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFIInDraftState', visibility: true }));
        return;
      }

      if (rfiRequestForm.state === RFXRequestState.DISCARDED) {
        const transitionReason = extractPropertyOrElseDefault(
          rfxRequest.properties,
          RequestPropertyIds.TransitionReason,
          ''
        )
        dispatch(setInitializedState({ username, rfiRequestForm }));
        dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFIInDiscardedState', visibility: true }));
        dispatch(setTransitionReason(transitionReason));
        return;
      }

      const auditRecords = await getRFXRequestAuditRecords({ apolloClient, rfxId, revision: 1 });
      const rfxQuestions = await getRFXQuestionsForRequest({ apolloClient, rfxId, revision: 1 });
      const vendorQuestionMap = createVendorQuestionMap(rfxQuestions);
      const vendorStatusArray = generateVendorStatusArray(vendorQuestionMap, rfxRequest);

      dispatch(
        setInitializedState({
          username,
          rfiRequestForm,
          auditRecords,
          vendorQuestionMap,
          correspondenceTableItems: vendorStatusArray
        })
      );
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setRFIBuyerDetailViewError({ errorKey: 'initiatingDetailViewError', error: sanitizedError }));
    } finally {
      dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isInitiatingDetailView', status: false }));
    }
  }
);

/**
 * Buyer send response message by clicking button.
 *
 * This will create RFXQuestion and put it in backend. It will continue to check attachments, and created them.
 *
 * Since this is an async operation, it will create a pendingQuestion in redux. The front-end will reload the questions
 * until the pendingQuestion is in the list.
 */
export const onBuyerSendMessage = createAsyncThunk<
  void,
  OnSendRFIMessageUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onBuyerSendMessage`,
  async ({ apolloClient, message, text, stagingArns }, { dispatch, getState }): Promise<void> => {
    try {
      dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isSendingMessage', status: true }));

      const { username, selectedVendorId: vendorId, rfiRequestForm } = getState().rfiBuyerDetailView;
      const { fullRfxId } = rfiRequestForm.generalInformation;

      const question = createBuyerRFIQuestion({ rfxRequestId: fullRfxId, username, vendorId, message, text });
      const newDocuments = await createRFIReplyContent({ apolloClient, question, stagingArns });

      dispatch(setPendingQuestion({ question, documents: newDocuments }));
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setRFIBuyerDetailViewError({ errorKey: 'sendMessageError', error: sanitizedError }));
      dispatch(clearPendingQuestion());
    } finally {
      dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isSendingMessage', status: false }));
    }
  }
);

/**
 * Buyer send response message to ALL_USER by clicking button.
 *
 * This will create RFXQuestion and put it in backend. It will continue to check attachments, and created them.
 *
 * Since this is an async operation, it will create a pendingQuestion in redux. The front-end will reload the questions
 * until the pendingQuestion is in the list.
 */
export const onBuyerSendReplyAllMessage = createAsyncThunk<
  void,
  OnSendRFIMessageUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(
  `${SLICE_NAME}/onBuyerSendReplyAllMessage`,
  async ({ apolloClient, message, text, stagingArns }, { dispatch, getState }): Promise<void> => {
    try {
      dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isSendingMessage', status: true }));

      const { username, rfiRequestForm } = getState().rfiBuyerDetailView;
      const { fullRfxId } = rfiRequestForm.generalInformation;
      const vendorId = 'ALL_USER';
      const question = createBuyerRFIQuestion({ rfxRequestId: fullRfxId, username, vendorId, message, text });
      const newDocuments = await createRFIReplyContent({ apolloClient, question, stagingArns });

      dispatch(setPendingQuestion({ question, documents: newDocuments }));
    } catch (error) {
      const sanitizedError = convertSystemErrorToString(error);
      dispatch(setRFIBuyerDetailViewError({ errorKey: 'sendMessageError', error: sanitizedError }));
      dispatch(clearPendingQuestion());
    } finally {
      dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isSendingMessage', status: false }));
    }
  }
);

/**
 * Refresh page to check whether the pending question is created, and all the pending documents are attached to the question.
 * If so, clear pending items, and reload all the questions. Otherwise, do nothing, and UI should continue re-try logic.
 */
export const onRefreshBuyerDetailView = createAsyncThunk<
  void,
  UIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(`${SLICE_NAME}/onRefreshBuyerDetailView`, async ({ apolloClient }, { dispatch, getState }): Promise<void> => {
  try {
    dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isRefreshingPage', status: true }));
    const { pendingQuestion, rfiRequestForm } = getState().rfiBuyerDetailView;
    if (pendingQuestion === null) {
      return;
    }

    const { question, documents } = pendingQuestion;

    const { fullRfxId: rfxId } = rfiRequestForm.generalInformation;

    // TODO: currently, this call only load 100 messages. Need to add pagination logic to support
    // more than 100 messages.
    const allQuestions = await getRFXQuestionsForRequest({ apolloClient, rfxId, revision: 1 });

    if (hasNoPendingQuestionAndDocuments({ question, documents, allQuestions })) {
      dispatch(clearPendingQuestion());
      const vendorQuestionMap = createVendorQuestionMap(allQuestions);
      dispatch(setVendorQuestionMap(vendorQuestionMap));
    }
  } catch (error) {
    const sanitizedError = convertSystemErrorToString(error);
    dispatch(setRFIBuyerDetailViewError({ errorKey: 'refreshPageError', error: sanitizedError }));
  } finally {
    dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isRefreshingPage', status: false }));
  }
});

/**
 * Publish RFI
 */
export const onPublishRFI = createAsyncThunk<
  void,
  UIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(`${SLICE_NAME}/onPublishRFI`, async ({ apolloClient }, { dispatch, getState }): Promise<void> => {
  try {
    dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isPublishRFI', status: true }));

    const { generalInformation } = getState().rfiBuyerDetailView.rfiRequestForm;
    const { fullRfxId: rfxId } = generalInformation;
    const input: TransitionRFXRequestInput = {
      apolloClient,
      rfxId: generalInformation.fullRfxId,
      revision: 1,
      toStateName: 'Published'
    };

    await transitionRFXRequest(input);

    const rfxRequest = await loadRFXRequest({ apolloClient, rfxId, revision: 1 });
    const rfiRequestForm = convertRFXRequestToRFIRequestForm(rfxRequest);
    dispatch(setRFIRequestForm(rfiRequestForm));

    const auditRecords = await getRFXRequestAuditRecords({ apolloClient, rfxId, revision: 1 });
    const rfxQuestions = await getRFXQuestionsForRequest({ apolloClient, rfxId, revision: 1 });
    const vendorQuestionMap = createVendorQuestionMap(rfxQuestions);
    const vendorStatusArray = generateVendorStatusArray(vendorQuestionMap, rfxRequest);

    dispatch(
      setInitializedState({
        rfiRequestForm,
        auditRecords,
        vendorQuestionMap,
        correspondenceTableItems: vendorStatusArray
      })
    );

    // Hide publish modal
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'isPublishRFIModalVisible', visibility: false }));
    // Hide "RFI in draft state" alert
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFIInDraftState', visibility: false }));
    // Hide "RFI Transitioned" information Alert
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFITransitioned', visibility: false }));
    // Show "RFI published" information Alert
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFIPublished', visibility: true }));
  } catch (error) {
    const sanitizedError = convertSystemErrorToString(error);
    dispatch(setRFIBuyerDetailViewError({ errorKey: 'publishRFIError', error: sanitizedError }));
  } finally {
    dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isPublishRFI', status: false }));
  }
});

export interface OnTransitionRFIUIInput extends UIInput {
  // The state to transition to
  toState: RFXRequestState;
  // Reason for this transition, this is for auditing information
  reason: string;
}

/**
 * Transition RFI, the logic is similar to "onPublishRFI", but use different UI components
 */
export const onTransitionRFI = createAsyncThunk<
  void,
  OnTransitionRFIUIInput,
  {
    dispatch: AppDispatch;
    state: RootState;
  }
>(`${SLICE_NAME}/onTransitionRFI`, async ({ apolloClient, toState, reason }, { dispatch, getState }): Promise<void> => {
  try {
    dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isTransitionRFI', status: true }));

    const { generalInformation } = getState().rfiBuyerDetailView.rfiRequestForm;
    const { fullRfxId } = generalInformation;
    const input: TransitionRFXRequestInput = {
      apolloClient,
      rfxId: generalInformation.fullRfxId,
      revision: 1,
      toStateName: toState,
      reason
    };

    await transitionRFXRequest(input);

    const rfxRequest = await loadRFXRequest({ apolloClient, rfxId: fullRfxId, revision: 1 });
    const rfiRequestForm = convertRFXRequestToRFIRequestForm(rfxRequest);
    const transitionReason = extractPropertyOrElseDefault(
      rfxRequest.properties,
      RequestPropertyIds.TransitionReason,
      ''
    )
    dispatch(setRFIRequestForm(rfiRequestForm));
    // Hide transition modal
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'isTransitionModalVisible', visibility: false }));
    // Hide "RFI in draft state" alert
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFIInDraftState', visibility: false }));
    // Hide "RFI published" information Alert
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFIPublished', visibility: false }));
    // Show "RFI Transitioned" information Alert
    dispatch(setRFIBuyerDetailViewVisibility({ visibilityKey: 'showRFITransitioned', visibility: true }));
    dispatch(setTransitionReason(transitionReason));
  } catch (error) {
    const sanitizedError = convertSystemErrorToString(error);
    dispatch(setRFIBuyerDetailViewError({ errorKey: 'transitionError', error: sanitizedError }));
  } finally {
    dispatch(setRFIBuyerDetailViewStatus({ statusKey: 'isTransitionRFI', status: false }));
  }
});

export const selectRFIBuyerDetailViewVisibility =
  ({ visibilityKey }: { visibilityKey: keyof RFIBuyerDetailViewState['visibility'] }) =>
  (state: RootState): boolean =>
    state[SLICE_NAME].visibility[visibilityKey];

export const {
  setInitializedState,
  setRFIRequestForm,
  setTransitionToState,
  setTransitionReason,
  setSelectedVendorId,
  setPendingQuestion,
  clearPendingQuestion,
  setVendorQuestionMap,
  setRFIBuyerDetailViewStatus,
  setRFIBuyerDetailViewError,
  setRFIBuyerDetailViewVisibility
} = rfiBuyerDetailViewSlice.actions;

export default rfiBuyerDetailViewSlice.reducer;
